import { useMemo } from 'react';
import { ICard } from '@/common/integrations/menhir/employee/card/card.types';
import { IconProps } from '@/common/types/general';
import { getCardColorsByStatus } from '@/common/utils/card';
import { INTL_MONTH_YEAR_2DIGIT_FORMAT, intlFormat } from '@/common/utils/date';
import classNames from 'classnames';
import css from './NewCoverflexCardBackIcon.module.css';

interface IProps extends IconProps {
  card?: ICard;
  activation?: {
    highlightedDigit?: number;
    input: string;
  };
}

export interface ICardNumberProps {
  card?: ICard;
  activation?: IProps['activation'];
  x: number;
  y: number;
}

type HighlightType = 'none' | 'all' | number | null;

const placeholders = [0, 1, 2, 3];

const CardNumber = ({ card, activation, x, y }: ICardNumberProps) => {
  const digits = activation?.input
    .split('')
    .filter((digit) => !!digit.trim().length);

  const highlighted: HighlightType = useMemo(() => {
    if (!activation) {
      return null;
    }
    if (activation.highlightedDigit === -1 && digits?.length !== 4) {
      return 'none';
    }
    if (activation.highlightedDigit === -1 && digits?.length === 4) {
      return 'all';
    }
    return activation.highlightedDigit ?? null;
  }, [activation, digits]);

  const rectX = x + 86;
  const rectY = y - 21;
  const colors = getCardColorsByStatus(card?.status);
  const isCardActivated =
    card && ['active', 'locked', 'cancelled'].includes(card.status);
  const showDigits = !!card?.panLastDigits && !activation && isCardActivated;

  return (
    <g fill="none">
      <text
        fill={colors.letters ?? '#fff'}
        style={{ whiteSpace: 'pre' }}
        fontFamily="F37 Ginger Pro"
        fontSize="13"
        letterSpacing="1.77992px"
      >
        <tspan x={x} y={y} fillOpacity={!!activation ? '0.4' : undefined}>
          &#x2022;&#x2022;&#x2022;&#x2022; &#x2022;&#x2022;&#x2022;&#x2022;
          &#x2022;&#x2022;&#x2022;&#x2022;{' '}
        </tspan>
        {showDigits
          ? card.panLastDigits
          : placeholders.map((idx) => (
              <tspan
                key={idx}
                fillOpacity={
                  !activation || highlighted === 'all' || highlighted === idx
                    ? undefined
                    : '0.4'
                }
              >
                &#x2022;
              </tspan>
            ))}
      </text>
      {typeof highlighted === 'number' &&
        placeholders.includes(highlighted) && (
          <rect
            x={rectX + 6 + highlighted * 7}
            y={rectY}
            width="10"
            height="29"
            stroke="white"
            strokeWidth="2"
          />
        )}
      {(highlighted === 'all' || highlighted === 'none') && (
        <rect
          x={rectX}
          y={rectY}
          width="42"
          height="29"
          stroke="white"
          strokeWidth="2"
        />
      )}
    </g>
  );
};

export const NewCoverflexCardBackIcon = (props: IProps) => {
  const { card, activation, width, height, className } = props;
  const colors = getCardColorsByStatus(card?.status);

  return (
    <div
      className={classNames(css.wrapper, css[props.card?.status ?? 'active'])}
    >
      <svg
        width={width ?? 280}
        height={height ?? 445}
        className={className}
        viewBox="0 0 250 396"
        fill="none"
      >
        <g clipPath="url(#a)">
          <path
            fill={colors.background ?? '#FF8E7A'}
            d="M236 0H14A14 14 0 0 0 0 14v368a14 14 0 0 0 14 14h222a14 14 0 0 0 14-14V14a14 14 0 0 0-14-14Z"
          />
          {card?.holderName && (
            <switch>
              <foreignObject
                fontFamily="F37 Ginger Pro"
                letterSpacing="0px"
                x="81"
                y="108.8"
                width="165"
                height="32"
              >
                <p
                  style={{
                    color: colors.letters ?? 'white',
                    fontSize: '13px',
                    lineHeight: '16px',
                    letterSpacing: 0,
                    opacity: !!activation ? '0.4' : undefined,
                  }}
                >
                  {card.holderName.toUpperCase()}
                </p>
              </foreignObject>
              <text
                fill={colors.letters ?? 'white'}
                fillOpacity={!!activation ? '0.4' : undefined}
                style={{ whiteSpace: 'pre' }}
                fontFamily="F37 Ginger Pro"
                fontSize="13px"
                letterSpacing="0px"
              >
                <tspan x="81" y="108.8">
                  {card.holderName.toUpperCase()}
                </tspan>
              </text>
            </switch>
          )}
          {card?.holderCompanyName && (
            <switch>
              <foreignObject
                fontFamily="F37 Ginger Pro"
                letterSpacing="0px"
                x="81"
                y="154.25"
                width="165"
                height="32"
              >
                <p
                  style={{
                    color: colors.letters ?? 'white',
                    opacity: !!activation ? '0.4' : undefined,
                    fontSize: '13px',
                    lineHeight: '16px',
                    letterSpacing: 0,
                  }}
                >
                  {card.holderCompanyName.toUpperCase()}
                </p>
              </foreignObject>
              <text
                fill={colors.letters ?? 'white'}
                fillOpacity={!!activation ? '0.4' : undefined}
                style={{ whiteSpace: 'pre' }}
                fontFamily="F37 Ginger Pro"
                fontSize="13px"
                letterSpacing="0px"
              >
                <tspan x="81" y="154.25">
                  {card.holderCompanyName.toUpperCase()}
                </tspan>
              </text>
            </switch>
          )}

          <path fill="#000" d="M70 0H20v396h50V0Z" opacity=".1" />
          <g fill={colors.letters ?? '#fff'}>
            <path
              d="M8.27 33.5v-1.7a3.03 3.03 0 1 1 6.05 0v1.7H8.27Zm5.16-1.68a2.14 2.14 0 1 0-4.28 0v.76h4.28v-.76ZM12.39 24h.27v3.1a1.17 1.17 0 0 0 .97-1.2 1.3 1.3 0 0 0-.47-1.06l.46-.6a1.9 1.9 0 0 1 .8 1.62 2.03 2.03 0 0 1-4.06.1A1.92 1.92 0 0 1 12.4 24Zm-.37.88a1 1 0 0 0-.87 1.06 1.05 1.05 0 0 0 .87 1.11v-2.17ZM12.37 19.09a2.03 2.03 0 0 1 2.02 2c0 .46-.16.9-.47 1.25h.4v.86H8.27v-.86h2.56c-.3-.35-.47-.79-.47-1.25a2.02 2.02 0 0 1 2-2Zm0 .89a1.2 1.2 0 0 0-1.2 1.18 1.2 1.2 0 0 0 2.4 0 1.2 1.2 0 0 0-1.2-1.18ZM9.03 18.4a.56.56 0 1 1 0-1.11.56.56 0 0 1 0 1.12Zm1.4-.97h3.89v.86h-3.88v-.86ZM11.27 15.22h1.76c.34 0 .57-.12.57-.5 0-.16-.01-.33-.05-.49h.78c.06.24.08.49.08.73 0 .73-.44 1.12-1.21 1.12h-1.93v.61h-.83v-.61h-1.3v-.86h1.3v-1.06h.83v1.06Z"
              opacity=".2"
            />
          </g>
          <g fill={colors.letters ?? '#fff'}>
            <path d="M176.16 364.71a3.3 3.3 0 0 1 .97-2.32 3.25 3.25 0 0 1 2.32-.93 3.39 3.39 0 0 1 2.77 1.45l-1.38.83a1.73 1.73 0 0 0-1.4-.74 1.67 1.67 0 0 0-1.17.5 1.67 1.67 0 0 0-.5 1.18 1.68 1.68 0 0 0 1.68 1.67 1.61 1.61 0 0 0 1.44-.79l1.33.84c-.28.46-.66.84-1.13 1.11a3.36 3.36 0 0 1-2.83.18 3.25 3.25 0 0 1-1.78-1.75c-.19-.38-.3-.8-.32-1.23ZM182.85 364.71a3.17 3.17 0 0 1 .98-2.31 3.16 3.16 0 0 1 2.32-.92 3.26 3.26 0 0 1 2.95 2.05c.24.59.29 1.24.15 1.85a3.26 3.26 0 0 1-5.89 1.11 3.16 3.16 0 0 1-.51-1.78Zm4.94 0a1.64 1.64 0 0 0-3.25-.26 1.64 1.64 0 0 0 2.26 1.79 1.67 1.67 0 0 0 1.02-1.53h-.03ZM196.4 361.46l-2.48 6.47h-1.77l-2.46-6.48h1.8l1.54 4.48 1.59-4.47h1.78ZM202.6 364.71v.5h-4.66c.08.39.29.72.6.96.3.22.67.34 1.05.32a1.91 1.91 0 0 0 1.52-.66l1.05.86a3.02 3.02 0 0 1-2.6 1.26 3.3 3.3 0 0 1-1.28-.2 3.27 3.27 0 0 1-1.77-1.76c-.16-.4-.24-.84-.22-1.28a3.17 3.17 0 0 1 3.19-3.23c.42 0 .84.06 1.24.21a3.1 3.1 0 0 1 1.73 1.76c.13.4.18.84.15 1.26Zm-1.65-.66a1.34 1.34 0 0 0-.5-.87 1.4 1.4 0 0 0-.98-.25 1.55 1.55 0 0 0-1.57 1.12h3.05ZM207.65 361.5v1.56h-.12c-1.38 0-2.1.37-2.1 1.5v3.4h-1.68v-6.33h1.67v.55a2.7 2.7 0 0 1 1.99-.66l.24-.02ZM208.62 361.67v-1.41c0-1.33.63-2.17 1.94-2.17.42 0 .83.06 1.24.16v1.44l-.76-.07c-.51 0-.77.18-.77.84v1.21h1.05v1.55h-1.05v4.7h-1.65v-4.7M212.51 366.1v-7.9h1.63v7.51c0 .41.12.67.66.67h.25v1.44c-.23.07-.46.1-.7.1a1.73 1.73 0 0 1-1.37-.47c-.18-.18-.32-.4-.4-.63-.08-.24-.1-.48-.07-.73ZM221.85 364.7v.5h-4.65c.07.39.29.73.6.96.3.23.67.34 1.05.33a1.9 1.9 0 0 0 1.51-.67l1.06.87a3.02 3.02 0 0 1-2.6 1.25c-.44.02-.87-.05-1.28-.2a3.28 3.28 0 0 1-1.82-1.78c-.16-.4-.23-.84-.22-1.28a3.23 3.23 0 0 1 .9-2.28c.6-.6 1.42-.94 2.26-.95a3.14 3.14 0 0 1 2.31.9 3.03 3.03 0 0 1 .88 2.35Zm-1.65-.66a1.32 1.32 0 0 0-1.5-1.1c-.34 0-.67.1-.95.3-.28.19-.49.47-.58.8h3.03ZM228.52 361.45h-1.9l-1.24 1.83-1.21-1.88h-1.92l2.22 3.25-2.22 3.23h1.9l1.24-1.86 1.23 1.85h1.9l-2.11-3.23 2.1-3.2Z" />
          </g>
          <path
            fill="#ABB3C4"
            d="M95.95 370.47s.57 3.16.73 3.88c.27.95.41 1.93.4 2.92-.16.4-.48.97-1.13.89l-.65-.08s0 .56-1.31.4c-1.32-.15-1.37-.64-1.37-.64-.55.17-1.12.22-1.7.16-.56-.16-1.05-1.14-1.05-1.14s-.33.57-1.3.08a1.52 1.52 0 0 1-.81-1.54s-.07.73-.56-.08a2.43 2.43 0 0 1-.41-1.46s-1.3.41-1.46-.24a3.51 3.51 0 0 1 0-1.37s-1.45-.08-.8-1.22c.63-1.14 3.56-2.55 3.96-3.17.4-.62.24-1.3-.32-1.37a6.33 6.33 0 0 1-2.84-1.44c-.57-.65-1.38-.68-1.7-1.43a2.2 2.2 0 0 1 0-2.25c.49-.7.9-1.05.16-1.22-.72-.17-3.4-.15-3-.75a1.37 1.37 0 0 1 .9-.64 1.49 1.49 0 0 1-.94-1.31c0-1 .53-.85.53-.85a.52.52 0 0 1-.53-.6.62.62 0 0 1 .62-.6s-.66-1.05-.26-1.05l.42-.06s-.42-1.05-.09-1.27l.33-.18s-.33-1.43.48-1.85c.82-.42 1.06 0 1.06 0s-1.06-1.48-.24-2.29a1.04 1.04 0 0 1 .77-.33 1.05 1.05 0 0 1 .77.33s-1.47-1.05-.9-2.34c.57-1.29 2.02-.66 2.02-.66s-.64-1.45.42-1.85c1.05-.4 1.25.45 1.25.45-.19-.9-.13-1.83.16-2.7.49-1.52 1.21-.84 1.41 0 .2.84.7-.83.94-1.5.25-.66 1.3-1.45 1.7-.74.4.72.41 3.17.33 4.77-.09 1.6.97 7.13 1.21 8.7.05 1.08.21 2.14.49 3.17.32.61.8 1.24 1.38 1.2a4.35 4.35 0 0 0 2.67-2.34 6.4 6.4 0 0 1 2.2-1.73c1.29-.57 1.7-.08 2.46.09.66.1 1.33.1 1.98 0 .2 0-1.5.68-1.65.77-.16.08-.33.64-.61 1.28-.29.65-1.06.86-1.74 1.83-.69.97-1.5 1.45-1.26 2.31.24.85.57 1.63 1.46.93a6.18 6.18 0 0 1 3.28-1.54c2.02-.44 1.9.45 3.52-1.29 1.61-1.74 2.8-2.64 3.65-2.6.86.05.33.65.44.73.12.09.7 0 .9.2.2.2-.2.57 0 .77.2.2.93 0 1.13.25.2.24.45.97 0 1.21s-.12.53-.16.9c-.04.37-.85.7-1.5.98-.64.27-1.14.47-1.2.8a1.06 1.06 0 0 1-.95.6c-.44 0-1.41.17-1.61.7a1.8 1.8 0 0 1-1.06 1.25c-.73.33-1.78 0-2.1.69-.22.6-.66 1.09-1.22 1.38a2.1 2.1 0 0 0-1.26.89c-.32.57-1.25.85-1.58 1.05-.37.42-.78.81-1.21 1.17-.86.66-.93.73-1.63.82-.7.08-2.25-.14-2.02 1.17Z"
            opacity=".5"
          />
          <g fill={colors.letters ?? '#fff'}>
            <path
              d="M88.82 23.4c-.83.34-1.72.5-2.62.47a4.63 4.63 0 0 1-4.9-5.01 4.94 4.94 0 0 1 5.17-5.21c.81-.03 1.61.12 2.36.44l-.31 1.06a4.74 4.74 0 0 0-2-.42c-2.33 0-3.87 1.5-3.87 4.1 0 2.42 1.4 3.98 3.81 3.98.73 0 1.45-.13 2.11-.42l.25 1ZM95.5 19.09h-3.86v3.57h4.3v1.05h-5.57v-9.89h5.34v1.06h-4.07v3.16h3.86v1.05Z"
              opacity=".2"
            />
          </g>
          <g fill={colors.letters ?? '#fff'}>
            <path
              d="M165 16.76c-.38.15-.77.22-1.17.2a2.11 2.11 0 0 1-2.18-2.23 2.18 2.18 0 0 1 1.4-2.16c.29-.11.6-.16.9-.15.37-.01.73.06 1.06.2l-.14.47a2.03 2.03 0 0 0-.89-.18 1.63 1.63 0 0 0-1.72 1.81 1.6 1.6 0 0 0 1.7 1.77c.31 0 .63-.06.92-.18l.12.45ZM165.7 12.5h2.38v.47h-1.8v1.47h1.66v.47h-1.67v1.99h-.57v-4.4ZM171.97 16.76c-.37.15-.76.21-1.16.2a2.11 2.11 0 0 1-2.17-2.23 2.19 2.19 0 0 1 2.28-2.3c.36-.02.72.05 1.05.2l-.14.46c-.29-.13-.6-.18-.91-.17a1.63 1.63 0 0 0-1.72 1.81 1.6 1.6 0 0 0 .43 1.28 1.6 1.6 0 0 0 1.25.48c.32 0 .64-.05.93-.17l.16.44ZM173.25 12.5v4.4h-.57v-4.4h.57ZM175.55 14.92v.43h-1.62v-.43h1.62ZM176.81 12.5v2.6c0 1 .44 1.4 1.06 1.4.62 0 1.05-.43 1.05-1.4v-2.6h.58v2.53c0 1.35-.71 1.9-1.66 1.9s-1.59-.5-1.59-1.87V12.5h.56ZM180.96 15.51l-.45 1.39h-.6l1.56-4.4h.68l1.43 4.4h-.6l-.46-1.39h-1.56Zm1.44-.44-.43-1.26c-.1-.29-.16-.55-.23-.8-.06.26-.14.52-.22.78l-.44 1.24 1.32.04ZM186.53 14.84h-1.72v1.58h1.93v.48h-2.47v-4.4h2.38v.47h-1.82v1.4h1.72l-.02.47ZM190.42 16.9v-1.16h-1.97v-.38l1.89-2.71h.62v2.64h.6v.45h-.6v1.15h-.54Zm0-1.61V13.2l-.35.62-1.06 1.45h1.41ZM192.02 16.9v-.36l.45-.44c1.06-1.05 1.57-1.57 1.57-2.21a.74.74 0 0 0-.5-.8.75.75 0 0 0-.33-.04c-.34.02-.66.14-.9.36l-.19-.4c.34-.28.77-.43 1.2-.43a1.2 1.2 0 0 1 1.3 1.23c0 .78-.57 1.42-1.46 2.28l-.34.32h1.9v.47l-2.7.01ZM198.17 14.73c0 1.44-.54 2.23-1.48 2.23s-1.4-.78-1.4-2.18c-.01-1.4.6-2.2 1.47-2.2s1.4.8 1.4 2.15Zm-2.3.06c0 1.05.34 1.73.85 1.73.52 0 .87-.69.87-1.77 0-1.09-.27-1.73-.86-1.73s-.86.6-.86 1.77ZM198.83 16.23c.29.17.61.26.94.27.74 0 .97-.47.96-.82 0-.6-.54-.85-1.05-.85h-.32v-.43h.32c.42 0 .94-.22.94-.72s-.2-.64-.73-.64c-.31 0-.6.1-.86.28l-.15-.42c.34-.21.72-.32 1.11-.33.84 0 1.22.5 1.22 1.06a1.05 1.05 0 0 1-.78 1 1.11 1.11 0 0 1 .94 1.06c0 .68-.53 1.27-1.55 1.27-.38 0-.77-.1-1.1-.28l.11-.45ZM202.05 16.9v-.35l.45-.45c1.06-1.05 1.57-1.57 1.57-2.21a.76.76 0 0 0-.5-.8.76.76 0 0 0-.34-.04c-.33.02-.64.15-.9.36l-.18-.4c.34-.28.77-.43 1.2-.43a1.2 1.2 0 0 1 1.33 1.23c0 .78-.57 1.42-1.47 2.28l-.34.32h1.91v.47l-2.73.01ZM207.94 13.14h-1.63l-.16 1.05h.35c.32 0 .63.08.9.22a1.21 1.21 0 0 1 .62 1.06 1.47 1.47 0 0 1-1.58 1.45 2 2 0 0 1-1.06-.27l.14-.43c.28.15.6.23.9.23a.94.94 0 0 0 1.06-.92c0-.55-.38-.94-1.22-.94h-.6l.28-2.03h2.04l-.04.58ZM211.65 14.92v.43h-1.61v-.43h1.61ZM216.34 14.73c0 1.44-.52 2.23-1.46 2.23s-1.4-.78-1.41-2.18c-.03-1.4.62-2.2 1.47-2.2.86 0 1.4.8 1.4 2.15Zm-2.3.06c0 1.05.34 1.73.87 1.73s.87-.69.87-1.77c0-1.09-.27-1.73-.87-1.73s-.87.6-.87 1.77ZM217.21 16.5h.47c.33-.04.64-.18.88-.41.32-.3.52-.7.56-1.13a1.29 1.29 0 0 1-1.06.45 1.23 1.23 0 0 1-1.25-1.3 1.46 1.46 0 0 1 1.46-1.5c.86 0 1.4.7 1.4 1.79a2.69 2.69 0 0 1-.74 2c-.34.3-.77.5-1.23.55-.18.02-.36.02-.53 0l.04-.45Zm.21-2.45a.8.8 0 0 0 .2.64.81.81 0 0 0 .61.27.97.97 0 0 0 .85-.45.38.38 0 0 0 0-.2c0-.74-.27-1.28-.87-1.28a.91.91 0 0 0-.77.66.91.91 0 0 0-.02.36ZM219.93 17.13l1.82-4.74h.44l-1.82 4.74h-.44ZM222.47 16.9v-.36l.45-.44c1.05-1.05 1.57-1.57 1.58-2.21a.76.76 0 0 0-.5-.8.76.76 0 0 0-.34-.04c-.33.02-.65.14-.9.36l-.18-.4c.33-.28.76-.43 1.2-.43a1.2 1.2 0 0 1 1.3 1.23c0 .78-.57 1.42-1.47 2.28l-.33.32h1.9v.47l-2.7.01ZM228.65 14.73c0 1.44-.54 2.23-1.48 2.23s-1.4-.78-1.4-2.18c0-1.4.62-2.2 1.48-2.2.87 0 1.4.8 1.4 2.15Zm-2.31.06c0 1.05.33 1.73.86 1.73s.87-.69.87-1.77c0-1.09-.27-1.73-.87-1.73s-.86.6-.86 1.77ZM231.65 14.84H230v1.58h1.9v.48h-2.47v-4.4h2.37v.47H230v1.4h1.7l-.05.47Z"
              opacity=".3"
            />
          </g>
          <g fill={colors.letters ?? '#fff'}>
            <path
              d="M80.73 199.14a2.52 2.52 0 0 1 4.34-1.74l-.38.38a1.88 1.88 0 0 0-1.43-.64 2 2 0 0 0 0 3.98 2.12 2.12 0 0 0 1.4-.58l.34.39a2.52 2.52 0 0 1-4.22-1.8h-.05ZM88.7 200.3h-2.36l-.5 1.2h-.6l1.98-4.81h.6l1.98 4.82h-.63l-.47-1.2Zm-2.11-.52h1.94l-.97-2.39-.97 2.39ZM93.3 201.5l-1.24-2.1h-1.05v2.1h-.56v-4.81h1.7c1.21 0 1.68.53 1.68 1.35a1.23 1.23 0 0 1-1.19 1.31l1.28 2.12-.62.04Zm-2.27-2.64h1.2c.74 0 1.05-.32 1.05-.82 0-.51-.32-.83-1.06-.83h-1.19v1.65ZM94.8 196.72h1.34a2.42 2.42 0 1 1 0 4.82H94.8v-4.82Zm1.33 4.3a1.9 1.9 0 1 0 0-3.77h-.77v3.77h.77ZM105.56 196.72v4.79H105l-3.02-4.14v4.14h-.55v-4.82h.72l2.9 3.96v-3.96l.5.03ZM106.74 199.39v-2.66h.55v2.5c0 1.3.45 1.9 1.4 1.9s1.4-.6 1.4-1.9v-2.5h.56v2.66c0 1.52-.77 2.25-1.96 2.25-1.2 0-1.95-.75-1.95-2.25ZM116.6 196.72v4.79h-.56v-4.1l-1.55 2.26h-.54l-1.57-2.25v4.09h-.55v-4.82h.74l1.65 2.38 1.66-2.38.72.03ZM117.85 196.72h1.23c1.19 0 1.7.44 1.7 1.23a1.05 1.05 0 0 1-.78 1.06 1.14 1.14 0 0 1 .97 1.2c0 .9-.7 1.32-1.89 1.32h-1.23v-4.81Zm1.54 2.03c.52 0 .84-.23.84-.72 0-.5-.25-.79-1.05-.79h-.78v1.51h.99Zm-.2 2.26c.84 0 1.23-.34 1.23-.88 0-.53-.39-.85-1.05-.85h-.94v1.73h.76ZM121.93 196.72h2.85v.52h-2.3v1.51h1.92v.54h-1.92v1.72h2.3v.53h-2.85v-4.82ZM128.5 201.5l-1.23-2.1h-1.05v2.1h-.56v-4.81h1.7c1.2 0 1.67.53 1.67 1.35a1.23 1.23 0 0 1-1.18 1.31l1.28 2.12-.63.04Zm-2.26-2.64h1.19c.75 0 1.05-.32 1.05-.82 0-.51-.31-.83-1.05-.83h-1.2v1.65Z"
              opacity=".6"
            />
            <CardNumber card={card} activation={activation} x={81} y={222} />
          </g>
          <g fill={colors.letters ?? '#fff'}>
            {card?.expirationDate && (
              <text
                fillOpacity={!!activation ? '0.4' : undefined}
                style={{ whiteSpace: 'pre' }}
                fontFamily="F37 Ginger Pro"
                fontSize="13"
                letterSpacing="0px"
              >
                <tspan x="166.17" y="287.06">
                  {intlFormat(
                    card.expirationDate,
                    INTL_MONTH_YEAR_2DIGIT_FORMAT
                  )}
                </tspan>
              </text>
            )}
            <path
              d="m170.73 264.2-1.99 4.84h-.57l-2.01-4.83h.6l1.67 4.09 1.66-4.1h.64ZM173.88 267.8h-2.37l-.49 1.24h-.59l1.97-4.83h.6l1.97 4.83h-.6l-.49-1.24Zm-2.11-.53h1.94l-.97-2.39-.97 2.39ZM175.65 264.2h.56v4.3h2.4v.54h-2.96v-4.83ZM179.31 264.2h.56v4.84h-.55v-4.83ZM181.12 264.2h1.33a2.44 2.44 0 0 1 2.65 2.42 2.44 2.44 0 0 1-2.65 2.41h-1.33v-4.83Zm1.31 4.3a1.87 1.87 0 0 0 1.88-1.13 1.89 1.89 0 0 0-1.88-2.64h-.77v3.76h.78ZM188.56 264.73h-1.62v-.52h3.77v.52h-1.6v4.3h-.56v-4.3ZM195.3 264.2v4.84h-.55v-2.27h-2.79v2.27h-.56v-4.83h.56v2.02h2.79v-2.02h.56ZM199.38 269.04l-1.25-2.11h-1.05v2.1h-.55v-4.82h1.7c1.2 0 1.67.53 1.67 1.35a1.23 1.23 0 0 1-1.17 1.31l1.26 2.17h-.61Zm-2.28-2.66h1.2c.75 0 1.05-.32 1.05-.82 0-.51-.32-.83-1.06-.83h-1.19v1.65ZM200.8 266.87v-2.66h.56v2.5c0 1.3.45 1.9 1.4 1.9.97 0 1.41-.6 1.41-1.9v-2.5h.55v2.66c0 1.51-.77 2.25-1.95 2.25-1.19 0-1.97-.74-1.97-2.25Z"
              opacity=".6"
            />
          </g>
          <text
            fill={colors.letters ?? '#fff'}
            xmlSpace="preserve"
            fontFamily="F37 Ginger Pro"
            fontSize="13"
            letterSpacing="0"
            style={{ whiteSpace: 'pre' }}
          >
            <tspan x="81" y="286.83">
              •••
            </tspan>
          </text>
          <g fill={colors.letters ?? '#fff'}>
            <path
              d="M82.47 268.52c.6 0 .96-.3.96-.82 0-1.2-2.29-.8-2.29-2.38a1.32 1.32 0 0 1 1.5-1.29c.46 0 .91.16 1.27.46l-.3.44c-.28-.26-.66-.4-1.05-.38-.52 0-.9.24-.9.73 0 1.15 2.3.72 2.3 2.38a1.34 1.34 0 0 1-1.53 1.38 1.88 1.88 0 0 1-1.6-.83l.35-.37a1.56 1.56 0 0 0 1.3.68ZM84.95 264.11h2.84v.54h-2.32v1.51h1.92v.54h-1.92v1.7h2.29v.55h-2.8v-4.84ZM88.21 266.55a2.52 2.52 0 0 1 4.33-1.75l-.37.38a1.9 1.9 0 0 0-1.43-.63 1.98 1.98 0 1 0 1.39 3.4l.35.38a2.5 2.5 0 0 1-4.22-1.78h-.05ZM93.2 266.78v-2.67h.55v2.51c0 1.3.46 1.9 1.4 1.9.96 0 1.4-.6 1.4-1.9v-2.5h.57v2.66c0 1.52-.77 2.25-1.96 2.25-1.2 0-1.96-.73-1.96-2.25ZM101.1 268.95l-1.23-2.11h-1.05v2.1h-.55v-4.83h1.7c1.2 0 1.66.55 1.66 1.35a1.22 1.22 0 0 1-1.18 1.32l1.28 2.17h-.62Zm-2.26-2.65h1.19c.76 0 1.05-.32 1.05-.82 0-.51-.3-.83-1.05-.83h-1.2v1.65ZM102.6 264.11h.56v4.84h-.56v-4.84ZM105.48 264.65h-1.61v-.54h3.77v.54h-1.59v4.3h-.55l-.02-4.3ZM111.97 264.11l-1.79 2.67v2.17h-.56v-2.17l-1.78-2.67h.65l1.42 2.11 1.41-2.1h.65ZM113.95 266.55a2.53 2.53 0 0 1 4.32-1.75l-.37.38a1.9 1.9 0 0 0-1.43-.63 1.98 1.98 0 1 0 1.39 3.4l.35.38a2.5 2.5 0 0 1-4.22-1.78h-.04ZM118.56 266.55a2.52 2.52 0 1 1 2.52 2.49 2.48 2.48 0 0 1-2.52-2.5Zm4.48 0a1.96 1.96 0 1 0-3.92.02 1.96 1.96 0 0 0 3.92-.02ZM124.5 264.13h1.34a2.42 2.42 0 0 1 2.65 2.41 2.42 2.42 0 0 1-2.65 2.42h-1.33v-4.83Zm1.34 4.29a1.88 1.88 0 0 0 2.08-1.88 1.89 1.89 0 0 0-1.3-1.8c-.26-.08-.52-.1-.78-.08h-.8v3.76h.8ZM129.35 264.11h2.85v.54h-2.3v1.51h1.92v.54h-1.92v1.7h2.3v.55h-2.85v-4.84Z"
              opacity=".6"
            />
          </g>
        </g>
        <defs>
          <clipPath id="a">
            <rect width="250" height="396" fill="#fff" rx="14" />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
};
